import { daysOfTheWeek, timeOptions } from "@plasma/data/time";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Label } from "../ui/label";
import { SelectCombobox } from "../ui/select-combobox";
import { Switch } from "../ui/switch";

interface AvailabilityTime {
  dayOfTheWeek: string;
  startTime: string;
  endTime: string;
}

/**
 * The type this component expects - for discount and category availability times
 */
export interface AvailabilityTimesForm {
  availabilityTimes: AvailabilityTime[];
}

/**
 * AvailabilityTimesFields - Type-safe component for discount/category availability times
 *
 * Must be rendered inside a react-hook-form `<Form>` provider whose values include
 * `availabilityTimes`. Used by discounts and category modals.
 */
export function AvailabilityTimesFields({
  showLabel = true,
}: {
  showLabel?: boolean;
}) {
  const form = useFormContext<AvailabilityTimesForm>();

  const { fields, append } = useFieldArray({
    control: form.control,
    name: "availabilityTimes",
  });

  const errorMessage =
    form.formState.errors.availabilityTimes &&
    typeof form.formState.errors.availabilityTimes === "object" &&
    "message" in form.formState.errors.availabilityTimes &&
    typeof form.formState.errors.availabilityTimes.message === "string"
      ? form.formState.errors.availabilityTimes.message
      : undefined;

  const fieldsWithIndex: Array<(typeof fields)[number] & { index: number }> =
    [];
  for (let index = 0; index < fields.length; index++) {
    const field = fields[index];
    if (field === undefined) {
      continue;
    }
    fieldsWithIndex.push({ ...field, index });
  }

  return (
    <div className="w-md space-y-5">
      {showLabel && <p className="label text-foreground">Availability times</p>}
      {daysOfTheWeek.map((day) => {
        const availabilityTimes = fieldsWithIndex.filter(
          (f) => f.dayOfTheWeek === day
        );

        return (
          <div className="flex gap-4" key={day}>
            <div className="flex h-12 w-32 items-center space-x-2">
              <Switch
                checked={availabilityTimes.length > 0}
                id={day}
                onCheckedChange={(value) => {
                  if (value) {
                    const currentValues = form.getValues("availabilityTimes");
                    form.setValue("availabilityTimes", [
                      ...currentValues,
                      {
                        dayOfTheWeek: day,
                        startTime: "12:00am",
                        endTime: "12:30am",
                      },
                    ]);
                  } else {
                    const currentValues = form.getValues("availabilityTimes");
                    form.setValue(
                      "availabilityTimes",
                      currentValues.filter((f) => f.dayOfTheWeek !== day)
                    );
                  }
                }}
              />
              <Label
                className="body-sm text-foreground leading-tight"
                htmlFor={day}
              >
                {day}
              </Label>
            </div>
            <div className="space-y-4">
              {availabilityTimes.map((availabilityTime, i) => {
                const startTimeWatch = form.watch(
                  `availabilityTimes.${availabilityTime.index}.startTime`
                );
                const endTimeWatch = form.watch(
                  `availabilityTimes.${availabilityTime.index}.endTime`
                );
                const startTimeIndex = timeOptions.indexOf(
                  startTimeWatch ?? ""
                );
                const endTimeList =
                  startTimeIndex > -1
                    ? timeOptions.slice(startTimeIndex + 1)
                    : [];

                return (
                  <div
                    className="flex items-center gap-3"
                    key={availabilityTime.index}
                  >
                    <FormField
                      control={form.control}
                      name={`availabilityTimes.${availabilityTime.index}.startTime`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="sr-only">Start time</FormLabel>
                          <FormControl>
                            <SelectCombobox
                              className="w-32"
                              onChange={(value: string | null) => {
                                field.onChange(value);

                                const selectedStartTimeIndex =
                                  timeOptions.indexOf(value ?? "");
                                const endTimeIndex = timeOptions.indexOf(
                                  endTimeWatch ?? ""
                                );
                                if (selectedStartTimeIndex > endTimeIndex) {
                                  form.setValue(
                                    `availabilityTimes.${availabilityTime.index}.endTime`,
                                    timeOptions[selectedStartTimeIndex + 1] ??
                                      ""
                                  );
                                }
                              }}
                              options={timeOptions}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Minus className="text-foreground size-5" />
                    <FormField
                      control={form.control}
                      name={`availabilityTimes.${availabilityTime.index}.endTime`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="sr-only">End time</FormLabel>
                          <FormControl>
                            <SelectCombobox
                              className="w-32"
                              onChange={field.onChange}
                              options={endTimeList}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {i === 0 ? (
                      <button
                        onClick={() => {
                          append({
                            startTime: "12:00am",
                            endTime: "12:00am",
                            dayOfTheWeek: day,
                          });
                        }}
                        type="button"
                      >
                        <Plus className="text-foreground size-5" />
                        <span className="sr-only">
                          Add new time period on {day}
                        </span>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          const currentValues =
                            form.getValues("availabilityTimes");
                          form.setValue(
                            "availabilityTimes",
                            currentValues.filter(
                              (_, index) => index !== availabilityTime.index
                            )
                          );
                        }}
                        type="button"
                      >
                        <Trash2 className="text-destructive size-5" />
                        <span className="sr-only">Delete this time period</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {errorMessage && (
        <p className="body-sm text-destructive text-center">{errorMessage}</p>
      )}
    </div>
  );
}
